/**
 * Moonou Hero — Cinematic Reveal Web Component
 *
 * Splits heading text into individual characters using the Splitting library
 * and applies a staggered cinematic blur/translate/scale animation.
 * Manages entrance/exit animations for slide transitions (stars, heading, subheadline).
 */

class CinematicReveal extends HTMLElement {
  constructor() {
    super();

    this.loadPromise = new Promise((resolve) => {
      this.resolveLoad = resolve;
    });
  }

  connectedCallback() {
    if (theme.config.motionReduced) {
      this.classList.remove('js-invisible');
      this.resolveLoad();
      return;
    }

    if (!document.body.hasAttribute('data-title-animation')) {
      this.classList.remove('js-invisible');
      this.resolveLoad();
      return;
    }

    this.init();
    this.setupSlideTransition();
  }

  init() {
    // Handle richtext output: strip <p> wrappers and convert to line-break spans
    let html = this.innerHTML.trim();

    if (/<p[^>]*>/i.test(html)) {
      html = html
        .replace(/<\/p>\s*<p[^>]*>/g, '<br>')
        .replace(/^<p[^>]*>/, '')
        .replace(/<\/p>$/, '');
    }

    // Prevent HighlightedText web component from auto-animating;
    // we manage the .animated class timing ourselves
    html = html.replace(/is="highlighted-text"/g, 'data-highlighted');

    if (/<br\s*\/?>/i.test(html)) {
      const lines = html.split(/<br\s*\/?>/gi);
      this.replaceChildren();
      lines.forEach((line) => {
        const span = document.createElement('span');
        span.className = 'mh-line';
        span.innerHTML = line.trim();
        this.appendChild(span);
      });
    } else {
      const temp = document.createElement('template');
      temp.innerHTML = html;
      this.replaceChildren(...temp.content.childNodes);
    }

    // Preserve SVG icons (e.g. scribble highlights) before Splitting,
    // because Splitting wraps text nodes inside SVG and corrupts them
    const savedSvgs = [];
    this.querySelectorAll('.highlighted-text svg.icon').forEach((svg) => {
      savedSvgs.push({ svg, parent: svg.parentElement });
      svg.remove();
    });

    Splitting({ target: this, by: 'chars' });

    // Restore SVG icons after Splitting
    savedSvgs.forEach(({ svg, parent }) => parent.appendChild(svg));

    // Calculate subheading delay based on heading character count
    const charCount = this.querySelectorAll('.char').length;
    const subheadingDelay = 300 + (charCount * 40) + 200;
    const parent = this.closest('.slideshow-word') || this.closest('.banner__box');
    if (parent) {
      parent.style.setProperty('--mh-subheading-delay', `${subheadingDelay}ms`);
    }

    // Trigger highlight animation after cinematic reveal completes
    this.highlightEls = this.querySelectorAll('.highlighted-text');
    if (this.highlightEls.length > 0) {
      this.highlightDelay = subheadingDelay;
      setTimeout(() => {
        this.highlightEls.forEach((el) => el.classList.add('animated'));
      }, this.highlightDelay);
    }

    this.classList.remove('js-invisible');
    this.resolveLoad();
  }

  /**
   * Set up slide transition observers for entrance/exit animations.
   */
  setupSlideTransition() {
    this.slideParent = this.closest('.slideshow-word');
    if (!this.slideParent) return;

    // Watch aria-current for entrance animation replay
    this.ariaObserver = new MutationObserver(() => {
      if (this.slideParent.getAttribute('aria-current') === 'true') {
        this.resetAll();
      }
    });
    this.ariaObserver.observe(this.slideParent, {
      attributes: true,
      attributeFilter: ['aria-current']
    });

    // Listen for slide changes on slideshow-hero for exit animation
    const slideshow = this.closest('slideshow-hero');
    if (slideshow) {
      slideshow.addEventListener('slider:change', (e) => {
        const newIndex = e.detail.index;
        const myIndex = parseInt(this.slideParent.dataset.index);
        if (this.slideParent.getAttribute('aria-current') === 'true' && myIndex !== newIndex) {
          this.exitAll();
        }
      });
    }
  }

  /**
   * Start exit animation on heading characters.
   */
  exit() {
    this.classList.add('is-exiting');
    if (this.highlightEls) {
      this.highlightEls.forEach((el) => el.classList.remove('animated'));
    }
  }

  /**
   * Start exit animation on heading + sibling stars/subheadline.
   */
  exitAll() {
    this.exit();
    if (this.slideParent) {
      this.slideParent.querySelectorAll('.mh-animate-item').forEach((item) => {
        item.classList.add('is-exiting');
      });
    }
  }

  /**
   * Reset heading character animations (remove exit state, restart entrance).
   */
  reset() {
    this.classList.remove('is-exiting');
    this.querySelectorAll('.char').forEach((char) => {
      char.style.animation = 'none';
      char.offsetHeight; // force reflow
      char.style.animation = '';
    });
    // Replay highlight animation with delay after cinematic reveal
    if (this.highlightEls && this.highlightEls.length > 0) {
      this.highlightEls.forEach((el) => el.classList.remove('animated'));
      setTimeout(() => {
        this.highlightEls.forEach((el) => el.classList.add('animated'));
      }, this.highlightDelay || 0);
    }
  }

  /**
   * Reset all animations: heading chars + sibling stars/subheadline.
   */
  resetAll() {
    this.reset();
    if (this.slideParent) {
      this.slideParent.querySelectorAll('.mh-animate-item').forEach((item) => {
        item.classList.remove('is-exiting');
        item.style.animation = 'none';
        item.offsetHeight; // force reflow
        item.style.animation = '';
      });
    }
  }

  /**
   * Replay the cinematic reveal animation.
   */
  refresh() {
    this.reset();
  }
}

customElements.define('cinematic-reveal', CinematicReveal);
